import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { ProgramService } from './program.service';
import { ProgramUpdate } from './program.interface';
import { AddProgramComponent } from './add-program/add-program.component';
import { EditProgramComponent } from './edit-program/edit-program.component';
import { ModulService } from '../modul/modul.service';
import { LocatiiService } from '../locatii/locatii.service';
import { SpecializariService } from '../specializari/specializari.service';
import { OperatoriService } from '../operatori/operatori.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfigurareProgramComponent } from './configurare-program/configurare-program.component';
import { SloturiBlocareComponent } from './sloturi-blocare/sloturi-blocare.component';
import { ConfigurareSloturiComponent } from './configurare-sloturi/configurare-sloturi.component';

@Component({
    selector: 'app-program',
    templateUrl: './program.component.html',
    styleUrls: ['./program.component.scss']
})
export class ProgramComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    parameters: { key: string; value: string }[] = [
        { key: '-- Alegeți --', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    module!: Generic[];
    locatii!: Generic[];
    specializari!: Generic[];
    operatori!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredModule!: any[];
    moduleControl = new FormControl();

    filteredLocatii!: any[];
    locatiiControl = new FormControl();

    filteredSpecializari!: any[];
    specializariControl = new FormControl();

    filteredOperatori!: any[];
    operatoriControl = new FormControl();

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        modul_id: -1,
        locatie_id: -1,
        specializare_id: -1,
        specialist_id: -1,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            modul_id: new FormControl(-1),
            locatie_id: new FormControl(-1),
            specializare_id: new FormControl(-1),
            specialist_id: new FormControl(-1),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.filteredModule = this.module;
        this.moduleControl.setValue(null);

        this.filteredLocatii = this.locatii;
        this.locatiiControl.setValue(null);

        this.filteredSpecializari = this.specializari;
        this.specializariControl.setValue(null);

        this.filteredOperatori = this.operatori;
        this.operatoriControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.dgaspc.activitati.nomenclatoare.program.listDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private programService: ProgramService,
        private modulService: ModulService,
        private locatiiService: LocatiiService,
        private specializariService: SpecializariService,
        private operatoriService: OperatoriService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.loadModule();
        this.loadLocatii();
        this.loadSpecializari();
        this.loadOperatori();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.moduleControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredModule = this.module;
                    return of(this.module);
                }
                return this.filterModule(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredModule = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.locatiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredLocatii = this.locatii;
                    return of(this.locatii);
                }
                return this.filterLocatii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredLocatii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.specializariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSpecializari = this.specializari;
                    return of(this.specializari);
                }
                return this.filterSpecializari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSpecializari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredOperatori = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatori = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadModule() {
        this.isFilterLoading = true;

        this.modulService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.module = results;
                this.filteredModule = results;
            },
            error: (err) => {
                console.error('Error in get module active:', err);
            }
        });
    }

    filterModule(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.module.filter(modul =>
            this.normalizeString(modul.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onModulSelectClosed() {
        const selectedValue = this.moduleControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredModule.some(modul =>
                modul.id === this.searchParamsForm.get('modul_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.moduleControl.setValue(null);
                this.filteredModule = this.module;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadLocatii() {
        this.isFilterLoading = true;

        this.locatiiService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.locatii = results;
                this.filteredLocatii = results;
            },
            error: (err) => {
                console.error('Error in get locatii active:', err);
            }
        });
    }

    filterLocatii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.locatii.filter(locatie =>
            this.normalizeString(locatie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onLocatieSelectClosed() {
        const selectedValue = this.locatiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredLocatii.some(locatie =>
                locatie.id === this.searchParamsForm.get('locatie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.locatiiControl.setValue(null);
                this.filteredLocatii = this.locatii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadSpecializari() {
        this.isFilterLoading = true;

        this.specializariService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.specializari = results;
                this.filteredSpecializari = results;
            },
            error: (err) => {
                console.error('Error in get specializari active:', err);
            }
        });
    }

    filterSpecializari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.specializari.filter(specializare =>
            this.normalizeString(specializare.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSpecializareSelectClosed() {
        const selectedValue = this.specializariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSpecializari.some(specializare =>
                specializare.id === this.searchParamsForm.get('specializare_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.specializariControl.setValue(null);
                this.filteredSpecializari = this.specializari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadOperatori() {
        this.isFilterLoading = true;

        this.operatoriService.list().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.operatori = results;
                this.filteredOperatori = results;
            },
            error: (err) => {
                console.error('Error in get operatori active:', err);
            }
        });
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatori.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onOperatoriSelectClosed() {
        const selectedValue = this.operatoriControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredOperatori.some(operator =>
                operator.id_user === this.searchParamsForm.get('specialist_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.operatoriControl.setValue(null);
                this.filteredOperatori = this.operatori;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            pagingType: 'full_numbers',
            responsive: true,
            pageLength: 25,
            searching: false,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            order: [[0, 'asc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                Object.assign(dataTablesParameters, { ...this.searchParamsForm.value });

                this.http
                    .post<DataTablesResponse>(
                        this.dtLink, // datatable link, we need this
                        dataTablesParameters // parameters for filtering, send here
                    )
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Modul',
                    data: 'denumire_modul',
                },
                {
                    title: 'Locație',
                    data: 'denumire_locatie',
                },
                {
                    title: 'Specializare',
                    data: 'denumire_specializare',
                },
                {
                    title: 'Specialist',
                    data: 'nume_specialist',
                },
                {
                    title: 'Tip problemă',
                    data: 'denumire_tip_problema',
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `
                                <div class="d-flex align-left justify-content-left">
                                    <button type="button" class="action-btn edit ms-1">
                                        <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                                    </button>
                                    <button type="button" class="action-btn delete ms-1">
                                        <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                                    </button>
                                    <button class="action-btn config-settings ms-1" title="Configurare">
                                        <img class="btn-img" src="/assets/icons/settings-white.svg" alt="settings-white"/>
                                    </button>
                                    <button class="action-btn slots-block ms-1" title="Sloturi blocare">
                                        <img class="btn-img" src="/assets/icons/sloturi-blocare.svg" alt="sloturi-blocare"/>
                                    </button>
                                    <button type="button" class="action-btn signatures ms-1" title="Sloturi programare">
                                        <img class="btn-img" src="/assets/icons/clock-regular.svg" alt="clock-regular"/>
                                    </button>
                                </div>
                            `
                        );
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const configurareIcon = lastTd?.querySelector('.config-settings') as HTMLElement;
                const sloturiBlocareIcon = lastTd?.querySelector('.slots-block') as HTMLElement;
                const programareIcon = lastTd?.querySelector('.signatures') as HTMLElement;

                if (editIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.update'
                    //     )
                    // ) {
                    //     editIcon.style.display = 'none';
                    // }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.delete'
                    //     )
                    // ) {
                    //     deleteIcon.style.display = 'none';
                    // }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteProgram(id);
                    });
                }

                if (configurareIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.update'
                    //     )
                    // ) {
                    //     editIcon.style.display = 'none';
                    // }
                    configurareIcon.addEventListener('click', () => {
                        this.openConfigurareDialog(data);
                    });
                }

                if (sloturiBlocareIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.update'
                    //     )
                    // ) {
                    //     editIcon.style.display = 'none';
                    // }
                    sloturiBlocareIcon.addEventListener('click', () => {
                        this.sloturiBlocare(data.id);
                    });
                }

                if (programareIcon) {
                    // if (
                    //     !this.permissionsService.hasPermission(
                    //         'auth-module.ghiseu-settings.ghiseu-module.update'
                    //     )
                    // ) {
                    //     editIcon.style.display = 'none';
                    // }
                    programareIcon.addEventListener('click', () => {
                        this.openConfigurareSloturiDialog(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
        this.showCard = false;
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddProgramComponent, {
            ...this.dialogService.getDialogConfig(),
            data: {
                module: this.module,
                locatii: this.locatii,
                specializari: this.specializari,
                operatori: this.operatori
            }
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Program adăugat cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditDialog(data: ProgramUpdate) {
        const dialogRef = this.dialog.open(EditProgramComponent, {
            ...this.dialogService.getDialogConfig(),
            data: {
                data: data,
                module: this.module,
                locatii: this.locatii,
                specializari: this.specializari,
                operatori: this.operatori
            },
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Program actualizat cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openConfigurareDialog(data: any) {
        const dialogRef = this.dialog.open(ConfigurareProgramComponent, {
            ...this.dialogService.getDialogConfig(),
            data
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Configurare actualizată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteProgram(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                // we do this because we need to have some control on activity
                this.deleteAction(id);
            }
        });
    }

    deleteAction(id: number) {
        this.isLoading = true;
        this.programService.delete(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Program șters cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteAction(id);
                    }
                });
            }
        );
    }

    sloturiBlocare(id: number) {
        const dialogRef = this.dialog.open(SloturiBlocareComponent, {
            width: '700px',
            maxWidth: '80vw',
            minHeight: '200px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: id,
        });
    }

    openConfigurareSloturiDialog(data: any) {
        const dialogRef = this.dialog.open(ConfigurareSloturiComponent, {
            ...this.dialogService.getDialogConfigMD(),
            data
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Sloturi programare actualizate cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }
}
